import Emitter from 'tiny-emitter'

export default class Component extends Emitter {
  /**
   * Creates a new Component instance.
   *
   * Immediately calls this methods in this order :
   *
   * - `this.bindMethods()`
   * - `this.getElems()`
   * - `this.events()`
   *
   * Events like `resize`, `pointermove`, `render` or `scroll` are automatically called from related `Page`.
   *
   * **Previous statement only works if linked to the page via it's `this.addComponent()` method**.
   *
   * @param {Element} el - The Component's DOM element.
   * @param {Boolean} [gui=false] - Whether this Component must show a GUI folder. You can also add a "data-gui" attribute to the element to enable it.
   *
   * @public
   * @constructor
   */
  constructor(el, gui) {
    super()

    this.el = el
    this.eventList = []
    this.tweens = []
    this.components = []
    this.destroyed = false
    this.initiator = null

    if (!el) return

    this.el.$component = this

    this.bindMethods()
    this.getElems()

    const components = this.createComponents()

    if (components && Array.isArray(components)) {
      for (const component of components) {
        this.addComponent(component)
      }
    } else if (components && !Array.isArray(components)) this.addComponent(components)

    this.debugMode(gui)
    this.events()
  }

  /**
   * Use this method to bind all the necessary methods of your class in case events are needed.
   *
   * Example `this.myMethod = this.myMethod.bind(this)`.
   *
   * @abstract
   * @returns {void}
   */
  bindMethods() {}

  /**
   * Query and save all the necessary DOM elements of your Block.
   *
   * @abstract
   * @returns {void}
   */
  getElems() {}

  /**
   * Create inner components.
   * Don't forget to return them in an array OR to call `this.addComponent()` to store them !
   *
   * @abstract
   * @returns {(void|Array<Component>)} An array of Components to be added to this Component.
   */
  createComponents() {}

  /**
   * Register all the necessary event on your saved DOM elements.
   * Use `this.addEvent()` to register a new event.
   *
   * @abstract
   * @returns {void}
   */
  events() {}

  /**
   * Called by Page.js when its appear animation is completed.
   *
   * @abstract
   * @returns {void}
   */
  onEnterCompleted() {
    for (const component of this.components) component.onEnterCompleted()
  }

  /**
   * Adds a new event to an element.
   * Stores the event to automatically remove it on page `destroy` method.
   *
   * @param {Object} options - The element to add the event to.
   * @param {(Element|Object|Array<Element|Object>)} options.target - The element/object to add the event to.
   * @param {String} options.name - The event name.
   * @param {Function} options.callback - The callback method called when the event is triggered.
   * @param {Boolean} [dom=true] - If `false`, the event will added using `.on` instead of `.addEventListener`.
   * @param {Boolean} [bind=this] - If given, the event callback will be bound to the value of `bind`. If the value is nullish, the callback will not be bound at all.
   *
   * @public
   * @returns {void}
   */
  addEvent({ target, name, callback, dom = true, bind = this } = {}) {
    if (!target || !name || !callback) return

    const fn = bind ? callback.bind(bind) : callback
    const targets = Array.isArray(target) ? target : [target]

    for (const el of targets) {
      if ((dom && !el.addEventListener) || (!dom && !el.on)) continue

      this.eventList.push({
        target: el,
        name,
        fn,
        dom,
        destroyed: false
      })

      if (dom) el.addEventListener(name, fn)
      else el.on(name, fn)
    }
  }

  /**
   * Stores a gsap tween/timeline/scrolltrigger to automatically kill it on page `destroy` method.
   *
   * @param {(Tween|Timeline|ScrollTrigger)} tween - The gsap tween (or a ScrollTrigger) to store.
   *
   * @public
   * @returns {void}
   */
  addTween(tween) {
    if (!tween || !tween.kill) return

    this.tweens.push(tween)
  }

  /**
   * Stores a Component instance in this Component, enabling all future calls to events (pointermove, resize, destroy, etc).
   *
   * @param {Component} component - A Component instance.
   *
   * @returns {void}
   */
  addComponent(component) {
    if (component instanceof Component) {
      this.components.push(component)

      if (!component.initiator) component.initiator = this
    }
  }

  /**
   * Resize event. Automatically called by the related `Page` instance.
   *
   * Already throttled (every 200ms).
   *
   * @abstract
   * @returns {void}
   */
  resize() {
    for (const component of this.components) component.resize()
  }

  /**
   * Scroll event. Automatically called by the related `Page` instance.
   *
   * @param {Event} e - The scroll event.
   *
   * @abstract
   * @returns {void}
   */
  scroll(e) {
    for (const component of this.components) component.scroll(e)
  }

  /**
   * ScrollEnded event. Automatically called by the related `Page` instance.
   *
   * @param {Event} e - The scroll event.
   *
   * @abstract
   * @returns {void}
   */
  scrollEnded(e) {
    for (const component of this.components) component.scrollEnded(e)
  }

  /**
   * Pointermove event. Automatically called by the related `Page` instance.
   *
   * The event is called from the `window` element, not the `this.el` element itself.
   *
   * @param {PointerEvent} e - The pointermove event object.
   *
   * @abstract
   * @returns {void}
   */
  pointermove(e) {
    for (const component of this.components) component.pointermove(e)
  }

  touchmove(e) {
    for (const component of this.components) component.touchmove(e)
  }

  /**
   * Update event. Automatically called by the related `Page` instance.
   *
   * Called once every frame (typically 60 times per second).
   *
   * @param {Number} time - The elapsed time since the first frame.
   * @param {Number} deltaTime - The delta between this frame and the last.
   *
   * @abstract
   * @returns {void}
   */
  render(time, deltaTime) {
    for (const component of this.components) component.render(time, deltaTime)
  }

  debugMode(gui) {
    if (window.DEV && window.DEV.gui && (gui || this.el.hasAttribute('data-gui'))) window.DEV.gui.createComponentGUI(this)
  }

  /**
   * Automatically called by the related `Page` instance when the `Page` is destroyed.
   *
   * Use it to remove event listener, destroy instances and release memory.
   * Automatically removes every event added via `this.addEvent()`.
   * Automatically kills every tween/timeline/scrolltrigger added via `this.addTween()`.
   *
   * @public
   * @returns {void}
   */
  destroy() {
    if (this.destroyed) return

    this.destroyed = true

    for (const event of this.eventList) {
      if (event.dom) event.target.removeEventListener(event.name, event.fn)
      else event.target.off(event.name, event.fn)

      event.destroyed = true
    }

    for (const tween of this.tweens) if (tween.kill) tween.kill()

    for (const component of this.components) component.destroy()

    if (this.gui) this.gui.dispose()
  }
}
